import type { ProjectWithJobType } from "@repo/app-types/entities/project";
import { SlideoverContainer } from "@repo/ui/Slideover/slideoverContainer";
import { type FC, useMemo, useState } from "react";
import { Heading } from "react-aria-components";
import { Outlet, useOutletContext } from "react-router";
import { useAllTrainingParticipantsForProjectInfiniteQuery } from "../../api/entities/trainingParticipant.js";
import { useModalNavigation } from "../../hooks/useModalNavigation.js";
import type { TreeItemKey } from "../projects/projectSettings/training/courseTreeHelpers.js";
import { ChapterAssessmentsSection } from "./chapterAssessmentsSection.js";
import { ParticipantsTable } from "./participantsTable.js";
import { TrainingChapterDialog } from "./trainingChapterDialog.js";
import { TrainingParticipantDialog } from "./trainingParticipantDialog.js";

export interface TrainingParticipantsTabProps {
  project: ProjectWithJobType;
}

/** What the tab passes down to its routed dialogs (via the outlet). */
interface TrainingParticipantsDialogContext {
  project: ProjectWithJobType;
  participantInitialSelectedKey: TreeItemKey | undefined;
}

export const TrainingParticipantDialogRoute: FC = () => {
  const { project, participantInitialSelectedKey } =
    useOutletContext<TrainingParticipantsDialogContext>();
  return (
    <TrainingParticipantDialog
      project={project}
      initialSelectedKey={participantInitialSelectedKey}
    />
  );
};

export const TrainingChapterDialogRoute: FC = () => {
  const { project } = useOutletContext<TrainingParticipantsDialogContext>();
  return <TrainingChapterDialog project={project} />;
};

export const TrainingParticipantsTab: FC<TrainingParticipantsTabProps> = (
  props,
) => {
  const {
    data,
    isLoading: isLoadingParticipants,
    isError: isErrorParticipants,
    hasNextPage,
    fetchNextPage,
  } = useAllTrainingParticipantsForProjectInfiniteQuery({
    projectId: props.project.id,
  });
  const participants = useMemo(
    () => data?.pages.flatMap((page) => page.participants),
    [data],
  );

  const [participantInitialSelectedKey, setParticipantInitialSelectedKey] =
    useState<TreeItemKey | undefined>();

  // TODO: Update this once we figure out the right pattern. Could `useModalNavigation` be route-aware so you can have some child routes be in a modal and other child routes not be in a modal?
  const modalNavigation = useModalNavigation();
  const dialogContext: TrainingParticipantsDialogContext = {
    project: props.project,
    participantInitialSelectedKey,
  };

  return (
    <>
      <div className="mx-auto max-w-4xl">
        <div className="-mx-4 space-y-4 gap-y-6 px-4 py-8 sm:-mx-6 sm:space-y-6 sm:px-6">
          <ChapterAssessmentsSection
            project={props.project}
            onChapterClick={(chapter) => {
              modalNavigation.push(
                `chapters/${encodeURIComponent(chapter.id)}`,
              );
            }}
          />

          <section className="space-y-4">
            <Heading className="text-base font-semibold text-foreground">
              All participants
            </Heading>

            <ParticipantsTable
              // TODO: Standardize how we deal with data vs. isLoading vs. isError and when to show a loader.
              participants={participants ?? []}
              isLoadingParticipants={isLoadingParticipants}
              isErrorParticipants={isErrorParticipants}
              onLoadMore={hasNextPage ? fetchNextPage : undefined}
              onParticipantAction={(participant, chapter) => {
                setParticipantInitialSelectedKey(
                  chapter
                    ? {
                        type: "chapter",
                        id: chapter.id,
                      }
                    : undefined,
                );
                modalNavigation.push(encodeURIComponent(participant.id));
              }}
            />
          </section>
        </div>
      </div>

      <SlideoverContainer
        isDismissable
        isOpen={modalNavigation.slideoverProps.isOpen}
        onOpenChange={modalNavigation.slideoverProps.onOpenChange}
      >
        <Outlet context={dialogContext} />
      </SlideoverContainer>
    </>
  );
};
